import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useMatches } from '../../hooks/useMatches'
import { usePoules } from '../../hooks/usePoules'
import { useTeams } from '../../hooks/useTeams'
import { SidebarRotator } from '../../components/layout/SidebarRotator'
import { ImageSlideshow } from '../../components/layout/ImageSlideshow'
import { Monitor, Calendar, List, Type, Image as ImageIcon, Volume2, VolumeX, Maximize, Minimize, QrCode } from 'lucide-react'
import { isDemoTournament, getDemoTournament, demoSlides } from '../../data/demoData'
import { soundFx } from '../../lib/soundEffects'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import { Badge, Button } from '../../components/ui'

export const TvPage = () => {
    const { id } = useParams()
    const { t } = useTranslation()
    const [slides, setSlides] = useState([])
    const [loading, setLoading] = useState(true)
    const [tournamentName, setTournamentName] = useState('')

    // Theme state
    const [theme, setTheme] = useState({
        color: '#4f46e5',
        background: '',
        logoLeft: '',
        logoRight: '',
        sport_type: 'poule'
    });

    // Player & Slide Loop State
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
    const [progress, setProgress] = useState(0)
    const [currentPouleIndex, setCurrentPouleIndex] = useState(0)
    const [isUnmuted, setIsUnmuted] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)

    // Data hooks
    const { matches } = useMatches(id)
    const { poules } = usePoules(id)
    const { teams } = useTeams(id)
    const [pouleStandings, setPouleStandings] = useState({})
    const [recentGoals, setRecentGoals] = useState({})
    const [tvSponsors, setTvSponsors] = useState([])

    const prevMatchesRef = useRef([])

    // Detect browser fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Load initial config & theme
    useEffect(() => {
        const fetchInitial = async () => {
            if (isDemoTournament(id)) {
                const dTourney = getDemoTournament(id);
                setTournamentName(dTourney.name || 'Demo Tournament')
                setTheme({
                    color: dTourney.theme_color || '#4f46e5',
                    background: dTourney.background_image || '',
                    logoLeft: dTourney.sponsor_left_image,
                    logoRight: dTourney.sponsor_right_image,
                    sport_type: id === '87654321-4321-4321-4321-210987654321' ? 'esports' : 'poule'
                })
                setSlides(demoSlides)
                setLoading(false)
                return
            }

            // Fetch Tournament Details
            const { data: tourneyData } = await supabase
                .from('tournaments')
                .select('name, theme_color, background_image, sponsor_left_image, sponsor_right_image, sport_type')
                .eq('id', id)
                .single()

            if (tourneyData) {
                setTournamentName(tourneyData.name || '')
                setTheme({
                    color: tourneyData.theme_color || '#4f46e5',
                    background: tourneyData.background_image || '',
                    logoLeft: tourneyData.sponsor_left_image,
                    logoRight: tourneyData.sponsor_right_image,
                    sport_type: tourneyData.sport_type || 'poule'
                })
            }

            // Fetch Slide Deck config
            const { data: slideData } = await supabase
                .from('slides')
                .select('*')
                .eq('tournament_id', id)
                .order('sort_order', { ascending: true })

            if (slideData) setSlides(slideData)

            // Fetch Sponsors
            try {
                const { data: sponsorsData } = await supabase
                    .from('sponsors')
                    .select('*')
                    .eq('tournament_id', id)
                    .eq('is_active', true)
                    .order('created_at', { ascending: true })

                if (sponsorsData) setTvSponsors(sponsorsData)
            } catch (err) {
                console.error('Error fetching tv sponsors:', err)
            }

            setLoading(false)
        }

        fetchInitial()

        // Realtime Subscription for slides (sync slide modifications live)
        const slidesChannel = supabase
            .channel(`slides_live_${id}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'slides',
                filter: `tournament_id=eq.${id}`
            }, async () => {
                console.log('⚡ Realtime Slides Update Detected!')
                const { data: slideData } = await supabase
                    .from('slides')
                    .select('*')
                    .eq('tournament_id', id)
                    .order('sort_order', { ascending: true })

                if (slideData) setSlides(slideData)
            })
            .subscribe()

        // Realtime Subscription for sponsors
        const sponsorsChannel = supabase
            .channel(`sponsors_live_tv_${id}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'sponsors',
                filter: `tournament_id=eq.${id}`
            }, async () => {
                console.log('⚡ Realtime TV Sponsors Update Detected!')
                const { data: sponsorsData } = await supabase
                    .from('sponsors')
                    .select('*')
                    .eq('tournament_id', id)
                    .eq('is_active', true)
                    .order('created_at', { ascending: true })

                if (sponsorsData) setTvSponsors(sponsorsData)
            })
            .subscribe()

        // Realtime Subscription for tournament theme changes
        const tournamentChannel = supabase
            .channel(`tournament_live_${id}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'tournaments',
                filter: `id=eq.${id}`
            }, async (payload) => {
                console.log('⚡ Realtime Tournament Update Detected!', payload.new)
                const data = payload.new;
                if (data) {
                    setTournamentName(data.name || '')
                    setTheme({
                        color: data.theme_color || '#4f46e5',
                        background: data.background_image || '',
                        logoLeft: data.sponsor_left_image,
                        logoRight: data.sponsor_right_image,
                        sport_type: data.sport_type || 'poule'
                    })
                }
            })
            .subscribe()

        return () => {
            supabase.removeChannel(slidesChannel)
            supabase.removeChannel(tournamentChannel)
            supabase.removeChannel(sponsorsChannel)
        }
    }, [id])

    const isEsports = theme.sport_type === 'esports';

    // Load Orbitron fonts dynamically for esports theme
    useEffect(() => {
        if (isEsports) {
            const link = document.createElement('link');
            link.href = 'https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@500;700&display=swap';
            link.rel = 'stylesheet';
            document.head.appendChild(link);
            return () => document.head.removeChild(link);
        }
    }, [isEsports]);

    // Live Score Flash Updates & Audio chimes
    useEffect(() => {
        if (prevMatchesRef.current.length > 0 && matches.length > 0) {
            const newlyUpdated = {};
            let playedSound = false;

            matches.forEach(match => {
                const prevMatch = prevMatchesRef.current.find(m => m.id === match.id);
                if (prevMatch) {
                    // Check if score changed or updated
                    const scoreHomeChanged = match.score_home !== prevMatch.score_home;
                    const scoreAwayChanged = match.score_away !== prevMatch.score_away;

                    if ((scoreHomeChanged && match.score_home !== null) || (scoreAwayChanged && match.score_away !== null)) {
                        newlyUpdated[match.id] = true;
                        if (!playedSound && isUnmuted) {
                            soundFx.play('success'); // Play premium C Major chime
                            playedSound = true;
                        }
                    }
                }
            });

            if (Object.keys(newlyUpdated).length > 0) {
                setRecentGoals(prev => ({ ...prev, ...newlyUpdated }));
                // Stop flashing match block after 10 seconds
                setTimeout(() => {
                    setRecentGoals(prev => {
                        const next = { ...prev };
                        Object.keys(newlyUpdated).forEach(mid => delete next[mid]);
                        return next;
                    });
                }, 10000);
            }
        }
        prevMatchesRef.current = matches;
    }, [matches, isUnmuted]);

    // Calculate Standings live
    useEffect(() => {
        if (teams.length === 0 || matches.length === 0) return

        const statsByPoule = {}
        teams.forEach(team => {
            const pId = team.poule_id || 'unknown'
            if (!statsByPoule[pId]) statsByPoule[pId] = []
            statsByPoule[pId].push({
                ...team,
                played: 0, won: 0, drawn: 0, lost: 0,
                goalsFor: 0, goalsAgainst: 0, goalDifference: 0, points: 0
            })
        })

        matches.forEach(match => {
            if (match.type !== 'group') return
            if (match.score_home === null || match.score_away === null) return
            Object.keys(statsByPoule).forEach(pId => {
                const h = statsByPoule[pId].find(t => t.id === match.home_team_id)
                const a = statsByPoule[pId].find(t => t.id === match.away_team_id)

                if (h) {
                    h.played++
                    h.goalsFor += match.score_home
                    h.goalsAgainst += match.score_away
                    if (match.score_home > match.score_away) { h.won++; h.points += 3 }
                    else if (match.score_home < match.score_away) h.lost++;
                    else { h.drawn++; h.points += 1 }

                    if (match.set_scores && Array.isArray(match.set_scores)) {
                        match.set_scores.forEach(s => {
                            h.gamesFor = (h.gamesFor || 0) + parseInt(s.home || 0)
                            h.gamesAgainst = (h.gamesAgainst || 0) + parseInt(s.away || 0)
                        })
                    }
                }

                if (a) {
                    a.played++
                    a.goalsFor += match.score_away
                    a.goalsAgainst += match.score_home
                    if (match.score_away > match.score_home) { a.won++; a.points += 3 }
                    else if (match.score_away < match.score_home) a.lost++;
                    else { a.drawn++; a.points += 1 }

                    if (match.set_scores && Array.isArray(match.set_scores)) {
                        match.set_scores.forEach(s => {
                            a.gamesFor = (a.gamesFor || 0) + parseInt(s.away || 0)
                            a.gamesAgainst = (a.gamesAgainst || 0) + parseInt(s.home || 0)
                        })
                    }
                }
            })
        })

        Object.keys(statsByPoule).forEach(pId => {
            statsByPoule[pId].forEach(t => {
                t.goalDifference = (t.gamesFor !== undefined) ? (t.gamesFor - t.gamesAgainst) : (t.goalsFor - t.goalsAgainst)
                t.isSetBased = (t.gamesFor !== undefined)
            })
            statsByPoule[pId].sort((a, b) => {
                if (b.points !== a.points) return b.points - a.points
                if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference
                return b.goalsFor - a.goalsFor
            })
        })

        setPouleStandings(statsByPoule)
    }, [matches, teams])

    // Dynamic Slide Deck computing
    const displaySlides = [...slides];
    if (tvSponsors && tvSponsors.length > 0) {
        displaySlides.push({
            id: 'dynamic-sponsors-ad-slide',
            type: 'sponsors_ad',
            title: 'Onze Sponsoren & Partners',
            duration: 12
        });
    }

    // --- AUTOMATED CINEMATIC SLIDESHOW LOOP ---
    useEffect(() => {
        let timer
        let progressTimer
        if (!isUnmuted) return; // Wait for unmute engagement
        if (displaySlides.length === 0) return;

        const currentSlide = displaySlides[currentSlideIndex]
        if (!currentSlide) return;

        const duration = (currentSlide.duration || 15) * 1000
        const step = 100
        let elapsed = 0

        // Reset progress and start interval
        setProgress(0);
        progressTimer = setInterval(() => {
            elapsed += step
            setProgress(Math.min((elapsed / duration) * 100, 100))
        }, step)

        // Slide transition timer
        timer = setTimeout(() => {
            const nextIndex = (currentSlideIndex + 1) % displaySlides.length
            const nextSlide = displaySlides[nextIndex]

            // Trigger beautiful synthesized transition tones
            if (nextSlide) {
                if (nextSlide.type === 'matches') {
                    soundFx.play('chord')
                } else if (nextSlide.type === 'program') {
                    soundFx.play('pop')
                } else if (nextSlide.type === 'custom') {
                    soundFx.play('focus')
                } else {
                    soundFx.play('navigate')
                }
            }

            setCurrentSlideIndex(nextIndex)
            setProgress(0)
            setCurrentPouleIndex(0) // Reset poule rotation
        }, duration)

        return () => {
            clearTimeout(timer)
            clearInterval(progressTimer)
        }
    }, [currentSlideIndex, displaySlides.length, isUnmuted])

    // --- SUB-SLIDE STANDINGS ROTATION (ROTATES MULTIPLE POULES WITHIN DURATION) ---
    useEffect(() => {
        let pouleTimer
        if (isUnmuted && displaySlides[currentSlideIndex]?.type === 'standings' && poules.length > 1) {
            const slideDuration = displaySlides[currentSlideIndex].duration || 15;
            // Distribute slide duration equally across poules (min 3.5s per poule)
            const timePerPoule = Math.max((slideDuration / poules.length) * 1000, 3500);

            pouleTimer = setInterval(() => {
                setCurrentPouleIndex(prev => (prev + 1) % poules.length)
            }, timePerPoule)
        } else {
            setCurrentPouleIndex(0)
        }
        return () => clearInterval(pouleTimer)
    }, [isUnmuted, currentSlideIndex, displaySlides, poules.length])

    // Generate News Ticker Marquee Items
    const getNewsTickerItems = () => {
        const items = [{ type: 'text', content: `🏆 ${t('presentation_page.tv_welcome') || 'Welkom bij'} ${tournamentName || '...'}!` }];

        matches
            .filter(m => m.score_home !== null)
            .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
            .slice(0, 5)
            .forEach(m => {
                items.push({
                    type: 'match',
                    home: m.home_team?.name || 'Home',
                    away: m.away_team?.name || 'Away',
                    score_home: m.score_home,
                    score_away: m.score_away,
                    logo_home: m.home_team?.logo_url,
                    logo_away: m.away_team?.logo_url
                });
            });

        poules.forEach(p => {
            if (pouleStandings[p.id]?.[0]) {
                items.push({ type: 'leader', poule: p.name, team: pouleStandings[p.id][0].name });
            }
        });

        const tips = [
            t('presentation_page.tv_tips_1') || '🌤️ Vergeet niet genoeg te drinken!',
            t('presentation_page.tv_tips_2') || '📢 Fair Play staat voorop!'
        ]
        tips.forEach(tip => items.push({ type: 'text', content: tip }));

        // Inject active sponsors into the news ticker footer
        if (tvSponsors && tvSponsors.length > 0) {
            tvSponsors.forEach(sponsor => {
                items.push({
                    type: 'sponsor_ad',
                    name: sponsor.name,
                    logo: sponsor.logo_url,
                    url: sponsor.website_url
                });
            });
        }

        return items;
    }

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                console.error("Error requesting fullscreen:", err);
            });
        } else {
            document.exitFullscreen();
        }
    };

    const handleStartPresentation = () => {
        soundFx.init();
        setIsUnmuted(true);
        soundFx.play('chord');
        // Automatically request fullscreen on start engagement
        document.documentElement.requestFullscreen().catch(() => {});
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-950 text-white flex flex-col items-center justify-center font-sans">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/10 border-t-purple-500 mb-4"></div>
                <p className="text-xl font-bold tracking-wider uppercase text-gray-400">{t('live_design_page.loading') || 'Laden...'}</p>
            </div>
        )
    }

    // engagement overlay to allow sound effects autoplay
    if (!isUnmuted) {
        return (
            <div className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center font-sans ${isEsports ? 'bg-[#05030a]' : 'bg-[#0f1115]'} text-white p-6`}>
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className={`absolute top-[-10%] left-[-10%] w-[45%] h-[45%] rounded-full blur-[120px] ${isEsports ? 'bg-purple-600/10' : 'bg-indigo-600/10'}`}></div>
                    <div className={`absolute bottom-[-10%] right-[-10%] w-[45%] h-[45%] rounded-full blur-[120px] ${isEsports ? 'bg-cyan-500/10' : 'bg-purple-600/10'}`}></div>
                </div>

                <div className={`max-w-md w-full text-center p-8 backdrop-blur-2xl rounded-[2.5rem] border shadow-2xl relative z-10 transition-all ${
                    isEsports ? 'bg-purple-950/15 border-purple-500/20 shadow-[0_0_50px_rgba(168,85,247,0.15)]' : 'bg-white/5 border-white/10'
                }`}>
                    <div className="flex justify-center mb-6">
                        <div className={`p-5 rounded-full border ${isEsports ? 'bg-purple-500/10 border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.2)]' : 'bg-indigo-500/20 border-indigo-500/30'}`}>
                            <Monitor size={56} className={isEsports ? 'text-purple-400 animate-pulse' : 'text-indigo-400 animate-pulse'} />
                        </div>
                    </div>
                    
                    <h1 className={`text-3xl font-black mb-3 tracking-tight ${isEsports ? 'text-white font-orbitron' : 'text-white'}`}>
                        {tournamentName || 'ProPoule TV'}
                    </h1>
                    
                    <p className="text-gray-400 mb-8 leading-relaxed">
                        {t('presentation_page.help_title') || 'TV Presentatiemodus'}
                    </p>

                    <Button 
                        size="lg" 
                        onClick={handleStartPresentation} 
                        className={`w-full py-6 text-lg font-bold rounded-2xl shadow-xl transition-all duration-300 hover:scale-105 active:scale-95 ${
                            isEsports 
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 border border-purple-500/30 shadow-purple-500/20 hover:shadow-purple-500/40 text-white' 
                                : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                        }`}
                    >
                        <Volume2 className="mr-2" size={20} />
                        {t('presentation_page.btn_start') || 'Start TV Presentatie'}
                    </Button>
                </div>
            </div>
        )
    }

    const slide = slides[currentSlideIndex]

    if (!slide) {
        return (
            <div className="fixed inset-0 bg-gray-950 text-white flex items-center justify-center z-[9999] font-sans">
                <div className="text-center p-8 bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 max-w-sm w-full">
                    <p className="text-2xl font-bold mb-4">{t('presentation_page.tv_no_slides') || 'Geen slides geconfigureerd'}</p>
                    <p className="text-sm text-gray-400 mb-6">Voeg slides toe in het dashboard onder "Presentatie & Speech".</p>
                    <Button variant="primary" onClick={() => setIsUnmuted(false)} className="w-full">
                        {t('presentation_page.btn_back') || 'Terug'}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className={`fixed inset-0 z-[9999] flex flex-col font-sans overflow-hidden select-none transition-colors duration-500 ${
            isEsports ? 'bg-[#05030a] text-white font-orbitron' : 'bg-[#0f1115] text-white'
        }`}>
            {/* Dynamic Cinematic Gradient Backdrop */}
            <div className="absolute inset-0 pointer-events-none">
                {theme.background ? (
                    <div 
                        className="absolute inset-0 opacity-15 bg-cover bg-center transition-all duration-1000 scale-105 animate-pulse-slow" 
                        style={{ backgroundImage: `url(${theme.background})` }}
                    />
                ) : null}
                <div className={`absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full blur-[120px] animate-pulse-slow ${
                    isEsports ? 'bg-purple-600/15' : 'bg-blue-600/20'
                }`}></div>
                <div className={`absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[120px] animate-pulse-slow ${
                    isEsports ? 'bg-cyan-500/15' : 'bg-purple-600/20'
                }`} style={{ animationDelay: '2s' }}></div>
                {isEsports && (
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(168,85,247,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.02)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                )}
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.dev/noise.svg')] opacity-[0.07]"></div>
            </div>

            {/* Glowing QR Code Floating Broadcast Widget */}
            <div className={`absolute top-6 right-16 p-3 rounded-2xl border backdrop-blur-xl shadow-2xl z-50 flex items-center gap-3 transition-all hover:scale-105 duration-300 ${
                isEsports 
                    ? 'bg-[#0b0717]/90 border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.25)]' 
                    : 'bg-black/70 border-white/10 shadow-[0_0_20px_rgba(255,255,255,0.05)]'
            }`}>
                <div className="flex flex-col text-right">
                    <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold leading-tight">{t('public.share_title') || 'Volg Live'}</span>
                    <span className={`text-xs font-black tracking-tight ${isEsports ? 'text-cyan-400 font-bold' : 'text-white'}`}>{t('presentation_page.tv_live_update') || 'Scan QR'}</span>
                </div>
                <div className={`p-1 bg-white rounded-lg ${isEsports ? 'shadow-[0_0_12px_rgba(6,182,212,0.4)]' : ''}`}>
                    <QRCodeSVG 
                        value={window.location.origin + '/live/' + id} 
                        size={52} 
                        level="H" 
                        includeMargin={false}
                        fgColor={isEsports ? '#05030a' : '#000000'}
                    />
                </div>
            </div>

            {/* Slide Progress Indicator */}
            <div className="h-1.5 bg-white/5 w-full z-50 relative">
                <div
                    className={`h-full shadow-[0_0_10px_rgba(168,85,247,0.5)] transition-all ease-linear duration-100 ${
                        isEsports 
                            ? 'bg-gradient-to-r from-purple-500 via-cyan-400 to-purple-500' 
                            : 'bg-gradient-to-r from-blue-400 to-purple-500'
                    }`}
                    style={{ width: `${progress}%`, backgroundColor: theme.color }}
                />
            </div>

            {/* Main Stage Panel */}
            <div className="flex-1 flex relative z-10 p-4 md:p-8 gap-6 overflow-hidden mb-16">
                {/* Left Sidebar (Rotator) */}
                <div className="hidden xl:flex flex-col w-72 shrink-0">
                    <div className={`h-full backdrop-blur-xl rounded-3xl p-6 border shadow-2xl flex flex-col justify-center transition-all ${
                        isEsports 
                            ? 'bg-purple-950/15 border-purple-500/20 shadow-[0_0_40px_rgba(168,85,247,0.15)]' 
                            : 'bg-white/5 border-white/10'
                    }`}>
                        {theme.logoLeft && (() => {
                            const parseImages = (jsonString) => {
                                try {
                                    if (jsonString.startsWith('[')) return JSON.parse(jsonString);
                                    return [jsonString];
                                } catch (e) { return [jsonString]; }
                            };
                            const images = parseImages(theme.logoLeft);
                            if (images.length > 0) return <SidebarRotator images={images} type="club" visibleCount={2} />;
                            return null;
                        })()}
                    </div>
                </div>

                {/* Central Cinematic Presenter */}
                <div className="flex-1 flex flex-col items-center justify-center relative w-full overflow-hidden">
                    
                    {/* CUSTOM TEXT SLIDE */}
                    {slide.type === 'custom' && (
                        <div className="w-full max-w-5xl animate-fade-in-up">
                            <div className={`backdrop-blur-2xl p-16 rounded-[2.5rem] text-center border shadow-2xl relative overflow-hidden transition-all duration-300 ${
                                isEsports 
                                    ? 'bg-purple-950/15 border-purple-500/20 shadow-[0_0_50px_rgba(168,85,247,0.15)]' 
                                    : 'bg-white/5 border-white/10'
                            }`}>
                                <div className={`absolute top-0 left-0 w-full h-1.5 ${
                                    isEsports ? 'bg-gradient-to-r from-purple-500 to-cyan-400' : 'bg-gradient-to-r from-orange-400 to-red-500'
                                }`}></div>
                                <h1 className="text-5xl md:text-7xl font-black mb-8 text-white tracking-tight leading-tight drop-shadow-xl">
                                    {slide.title}
                                </h1>
                                <div className="w-20 h-1 bg-white/10 rounded-full mx-auto mb-8"></div>
                                <p className="text-2xl md:text-4xl text-gray-200 leading-relaxed font-medium whitespace-pre-line">
                                    {slide.content}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* SPONSORS ADVERTISEMENT GRID SLIDE */}
                    {slide.type === 'sponsors_ad' && (
                        <div className="w-full max-w-5xl animate-fade-in-up flex flex-col justify-center h-full">
                            <div className={`backdrop-blur-2xl p-12 rounded-[2.5rem] border shadow-2xl relative overflow-hidden transition-all duration-300 ${
                                isEsports 
                                    ? 'bg-[#0b0717]/85 border-purple-500/20 shadow-[0_0_50px_rgba(168,85,247,0.15)]' 
                                    : 'bg-[#1c1f26]/90 border-white/10 shadow-[0_0_50px_rgba(255,255,255,0.02)]'
                            }`}>
                                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-600"></div>
                                
                                <div className="text-center mb-8 shrink-0">
                                    <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight leading-tight flex items-center justify-center gap-3">
                                        <Handshake className="text-emerald-400 shrink-0" size={36} />
                                        {slide.title}
                                    </h1>
                                    <p className="text-sm font-medium text-emerald-400/80 uppercase tracking-widest mt-2">Mogelijk gemaakt door onze fantastische toernooipartners</p>
                                    <div className="w-20 h-1 bg-white/10 rounded-full mx-auto mt-4"></div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-h-[45vh] overflow-y-auto pr-1 custom-scrollbar items-center py-4">
                                    {tvSponsors.map(sponsor => (
                                        <div 
                                            key={sponsor.id} 
                                            className={`p-6 rounded-2xl border flex flex-col items-center justify-center gap-4 transition-all duration-300 hover:scale-105 shadow-md ${
                                                isEsports
                                                    ? 'bg-purple-950/20 border-purple-500/20 hover:border-purple-500/40 hover:shadow-[0_0_20px_rgba(168,85,247,0.15)]'
                                                    : 'bg-white/5 border-white/5 hover:border-white/15'
                                            }`}
                                        >
                                            <div className="h-20 w-full bg-white/5 dark:bg-slate-950/20 rounded-xl p-3 flex items-center justify-center border border-white/5 overflow-hidden">
                                                {sponsor.logo_url ? (
                                                    <img 
                                                        src={sponsor.logo_url} 
                                                        alt={sponsor.name} 
                                                        className="max-h-full max-w-full object-contain filter drop-shadow-sm" 
                                                        onError={(e) => {
                                                            e.target.onerror = null
                                                            e.target.style.display = 'none'
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-lg flex items-center justify-center text-white font-bold text-sm uppercase shadow">
                                                        {sponsor.name.slice(0, 3)}
                                                    </div>
                                                )}
                                            </div>
                                            <span className="text-lg font-bold text-gray-200 tracking-tight truncate max-w-full text-center">
                                                {sponsor.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* IMAGE / SPONSOR DIA SLIDE */}
                    {slide.type === 'image' && (() => {
                        const parseContent = (c) => {
                            try {
                                if (c && c.startsWith('[')) return JSON.parse(c);
                                return c ? [c] : [];
                            } catch (e) { return c ? [c] : []; }
                        };
                        const images = parseContent(slide.image_url || slide.content);
                        if (images.length === 0) return null;
                        return (
                            <div className="w-full h-full flex items-center justify-center p-4">
                                <ImageSlideshow images={images} title={slide.title} />
                            </div>
                        )
                    })()}

                    {/* STANDINGS SLIDE (SINGLE POULE FOCUS) */}
                    {slide.type === 'standings' && (
                        <div className="w-full h-full flex flex-col animate-fade-in max-w-5xl mx-auto justify-center">
                            <header className="mb-6 flex items-center justify-between shrink-0">
                                <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-xl border ${
                                        isEsports ? 'bg-purple-500/10 border-purple-500/30 shadow-[0_0_15px_rgba(168,85,247,0.2)]' : 'bg-blue-500/20 border-blue-500/30'
                                    }`}>
                                        <List size={32} className={isEsports ? 'text-purple-400' : 'text-blue-400'} />
                                    </div>
                                    <div>
                                        <h2 className="text-4xl font-black text-white tracking-tight">{t('presentation_page.tv_standings') || 'Tussenstanden'}</h2>
                                        <p className={`text-lg font-medium ${isEsports ? 'text-purple-300/80' : 'text-blue-200/80'}`}>{t('presentation_page.tv_live_update') || 'Live Updates'}</p>
                                    </div>
                                </div>
                                {poules.length > 1 && (
                                    <div className="flex gap-2">
                                        {poules.map((_, idx) => (
                                            <div key={idx} className={`h-2 rounded-full transition-all duration-300 ${
                                                idx === currentPouleIndex 
                                                    ? isEsports ? 'w-8 bg-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.5)]' : 'w-8 bg-blue-500' 
                                                    : 'w-2 bg-white/20'
                                            }`} />
                                        ))}
                                    </div>
                                )}
                            </header>

                            <div className="flex-1 overflow-hidden relative max-h-[65vh]">
                                {poules.length > 0 && (() => {
                                    const poule = poules[currentPouleIndex] || poules[0]
                                    const standings = pouleStandings[poule.id] || []

                                    return (
                                        <div key={poule.id} className={`backdrop-blur-xl rounded-[2.5rem] p-8 border shadow-2xl flex flex-col h-full animate-fade-in transition-all ${
                                            isEsports 
                                                ? 'bg-[#0b0717]/85 border-purple-500/20 shadow-[0_0_50px_rgba(168,85,247,0.15)]' 
                                                : 'bg-[#1c1f26]/80 border-white/10'
                                        }`}>
                                            <div className="flex justify-between items-center mb-6 border-b border-white/5 pb-4 shrink-0">
                                                <h3 className="text-3xl font-black text-white truncate">{poule.name}</h3>
                                                <Badge className={`px-4 py-1.5 text-xs font-bold tracking-widest uppercase ${
                                                    isEsports ? 'bg-purple-600/30 text-purple-300 border border-purple-500/30 shadow-[0_0_10px_rgba(168,85,247,0.2)]' : 'bg-blue-600 text-white'
                                                }`}>{t('presentation_page.tv_poule') || 'POULE'}</Badge>
                                            </div>

                                            <div className="flex-1 overflow-hidden flex flex-col">
                                                {/* Header */}
                                                <div className="grid grid-cols-[4rem_1fr_6rem_6rem] gap-6 px-6 py-3 text-xs font-bold text-gray-400 uppercase tracking-widest bg-black/35 rounded-xl mb-3 shrink-0">
                                                    <div className="text-center text-sm">{t('standings_page.table_header_rank') || '#'}</div>
                                                    <div className="text-sm">{t('presentation_page.tv_team') || 'Team'}</div>
                                                    <div className="text-center text-sm">{t('presentation_page.tv_played') || 'GS'}</div>
                                                    <div className="text-center text-white text-sm">{t('presentation_page.tv_points') || 'PNT'}</div>
                                                </div>

                                                {/* Rows */}
                                                <div className="space-y-2.5 overflow-y-auto pr-1 custom-scrollbar">
                                                    {standings.map((team, idx) => (
                                                        <div
                                                            key={team.id}
                                                            className={`grid grid-cols-[4rem_1fr_6rem_6rem] gap-6 items-center px-6 py-3.5 rounded-2xl border transition-all ${
                                                                idx < 2
                                                                    ? isEsports
                                                                        ? 'bg-purple-500/15 border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.25)]'
                                                                        : 'bg-blue-500/10 border-blue-500/20 shadow-lg'
                                                                    : 'bg-white/5 border-transparent hover:bg-white/10'
                                                            }`}
                                                        >
                                                            <div className="flex justify-center">
                                                                <span className={`w-8 h-8 flex items-center justify-center rounded-xl text-base font-black leading-none ${
                                                                    idx === 0 
                                                                        ? isEsports 
                                                                            ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30 scale-105' 
                                                                            : 'bg-yellow-400 text-black shadow-lg scale-105' 
                                                                        : idx === 1 
                                                                            ? isEsports 
                                                                                ? 'bg-cyan-500 text-black shadow-md' 
                                                                                : 'bg-gray-300 text-black shadow-md' 
                                                                            : 'text-gray-500 bg-white/5'
                                                                }`}>
                                                                    {idx + 1}
                                                                </span>
                                                            </div>
                                                            <div className="font-bold text-xl text-gray-100 truncate min-w-0 flex items-center gap-4">
                                                                {team.logo_url && <img src={team.logo_url} alt="" className="w-8 h-8 object-contain drop-shadow-md bg-white/5 rounded-lg p-0.5" onError={(e) => e.target.style.display = 'none'} />}
                                                                <span className="truncate">{team.name || "Team ?"}</span>
                                                            </div>
                                                            <div className="text-center font-mono text-gray-400 text-xl">{team.played}</div>
                                                            <div className="text-center font-mono font-black text-white text-2xl tracking-tight">{team.points}</div>
                                                        </div>
                                                    ))}
                                                    {standings.length === 0 && <p className="text-gray-500 text-center italic py-12 text-lg">{t('presentation_page.tv_no_teams') || 'Nog geen teams'}</p>}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })()}
                            </div>
                        </div>
                    )}

                    {/* MATCHES SLIDE (LATEST RESULTS) */}
                    {slide.type === 'matches' && (
                        <div className="w-full h-full flex flex-col animate-fade-in px-4 justify-center">
                            <header className="mb-6 flex items-center justify-center text-center shrink-0">
                                <div>
                                    <div className={`inline-flex items-center justify-center p-3 rounded-full border mb-3 backdrop-blur-sm ${
                                        isEsports ? 'bg-purple-500/10 border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)]' : 'bg-green-500/10 border-green-500/20'
                                    }`}>
                                        <Monitor size={36} className={isEsports ? 'text-purple-400 animate-pulse' : 'text-green-400'} />
                                    </div>
                                    <h2 className="text-4xl font-black text-white tracking-tight drop-shadow-lg">{t('presentation_page.tv_latest_results') || 'Laatste Uitslagen'}</h2>
                                </div>
                            </header>

                            <div className="grid grid-cols-1 gap-3.5 max-w-5xl w-full mx-auto content-start overflow-y-auto max-h-[60vh] pr-1 custom-scrollbar">
                                {matches
                                    .filter(m => m.score_home !== null)
                                    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
                                    .slice(0, 5)
                                    .map((match) => {
                                        const isRecentlyGoal = recentGoals[match.id];
                                        return (
                                            <div
                                                key={match.id}
                                                className={`group relative border p-4 rounded-2xl shadow-xl grid grid-cols-[1fr_auto_1fr] gap-6 items-center transition-all duration-500 ${
                                                    isRecentlyGoal 
                                                        ? 'bg-green-950/20 border-green-500 animate-pulse shadow-[0_0_25px_rgba(74,222,128,0.35)] scale-[1.02]' 
                                                        : isEsports 
                                                            ? 'bg-[#0b0717]/85 border-purple-500/20 hover:border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.1)]' 
                                                            : 'bg-[#1c1f26] border-white/5 hover:border-white/15'
                                                }`}
                                            >
                                                {isRecentlyGoal && (
                                                    <div className="absolute top-[-10px] left-1/2 transform -translate-x-1/2 bg-green-500 text-black text-[10px] font-black uppercase tracking-widest px-3 py-0.5 rounded-full shadow-lg z-50">
                                                        ⚽ GOAL / LIVE UPDATE!
                                                    </div>
                                                )}

                                                <div className={`absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl ${
                                                    isRecentlyGoal 
                                                        ? 'bg-green-500' 
                                                        : isEsports ? 'bg-purple-500 shadow-[0_0_8px_#a855f7]' : 'bg-yellow-500'
                                                }`}></div>

                                                {/* Home Team */}
                                                <div className="flex items-center justify-end gap-4 min-w-0">
                                                    <span className={`text-xl md:text-2xl font-black block leading-tight text-right truncate ${match.score_home > match.score_away ? 'text-green-400 font-bold' : 'text-gray-100'}`}>
                                                        {match.home_team?.name || 'Home'}
                                                    </span>
                                                    {match.home_team?.logo_url ? (
                                                        <img src={match.home_team.logo_url} className="w-10 h-10 md:w-12 md:h-12 object-contain bg-white/5 rounded-lg p-0.5 shrink-0 shadow-md" onError={(e) => e.target.style.display = 'none'} />
                                                    ) : <div className="w-10 h-10 bg-white/5 rounded-full shrink-0" />}
                                                </div>

                                                {/* Score Box */}
                                                <div className={`px-5 py-2.5 rounded-xl border min-w-[120px] text-center shrink-0 shadow-[0_0_15px_rgba(0,0,0,0.6)] ${
                                                    isRecentlyGoal
                                                        ? 'bg-green-950/40 border-green-500'
                                                        : isEsports ? 'bg-[#150a2e]/60 border-purple-500/30' : 'bg-black/60 border-white/10'
                                                }`}>
                                                    <span className={`font-mono text-4xl md:text-5xl font-black tracking-widest ${
                                                        isRecentlyGoal ? 'text-green-400' : isEsports ? 'text-cyan-400' : 'text-yellow-400'
                                                    }`}>
                                                        {match.score_home}-{match.score_away}
                                                    </span>
                                                </div>

                                                {/* Away Team */}
                                                <div className="flex items-center justify-start gap-4 min-w-0">
                                                    {match.away_team?.logo_url ? (
                                                        <img src={match.away_team.logo_url} className="w-10 h-10 md:w-12 md:h-12 object-contain bg-white/5 rounded-lg p-0.5 shrink-0 shadow-md" onError={(e) => e.target.style.display = 'none'} />
                                                    ) : <div className="w-10 h-10 bg-white/5 rounded-full shrink-0" />}
                                                    <span className={`text-xl md:text-2xl font-black block leading-tight text-left truncate ${match.score_away > match.score_home ? 'text-green-400 font-bold' : 'text-gray-100'}`}>
                                                        {match.away_team?.name || 'Away'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                {matches.filter(m => m.score_home !== null).length === 0 && (
                                    <div className="text-center py-12">
                                        <p className="text-xl text-gray-500 font-bold">{t('presentation_page.tv_no_results') || 'Nog geen uitslagen bekend.'}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* PROGRAM SLIDE */}
                    {slide.type === 'program' && (
                        <div className="w-full h-full flex flex-col animate-fade-in px-4 justify-center">
                            <header className="mb-6 flex items-center justify-center text-center shrink-0">
                                <div>
                                    <div className={`inline-flex items-center justify-center p-3 rounded-full border mb-3 backdrop-blur-sm ${
                                        isEsports ? 'bg-purple-500/10 border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)]' : 'bg-purple-500/10 border-purple-500/20'
                                    }`}>
                                        <Calendar size={36} className={isEsports ? 'text-cyan-400' : 'text-purple-400'} />
                                    </div>
                                    <h2 className="text-4xl font-black text-white tracking-tight drop-shadow-lg">{t('presentation_page.tv_program') || 'Programma'}</h2>
                                </div>
                            </header>

                            <div className="grid grid-cols-1 gap-3.5 max-w-5xl w-full mx-auto content-start overflow-y-auto max-h-[60vh] pr-1 custom-scrollbar">
                                {matches
                                    .filter(m => m.score_home === null)
                                    .sort((a, b) => (a.match_time || '23:59').localeCompare(b.match_time || '23:59'))
                                    .slice(0, 5)
                                    .map((match) => (
                                        <div
                                            key={match.id}
                                            className={`border p-4 rounded-2xl shadow-xl grid grid-cols-[1fr_auto_1fr] gap-6 items-center hover:bg-[#232730]/40 transition-colors ${
                                                isEsports 
                                                    ? 'bg-[#0b0717]/85 border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.1)]' 
                                                    : 'bg-[#1c1f26] border-white/5'
                                            }`}
                                        >
                                            {/* Home Team */}
                                            <div className="flex items-center justify-end gap-4 min-w-0">
                                                <span className="text-xl md:text-2xl font-bold text-gray-300 block leading-tight text-right truncate">
                                                    {match.home_team?.name || 'Home'}
                                                </span>
                                                {match.home_team?.logo_url ? (
                                                    <img src={match.home_team.logo_url} className="w-10 h-10 md:w-12 md:h-12 object-contain bg-white/5 rounded-lg p-0.5 shrink-0 shadow-md animate-fade-in" onError={(e) => e.target.style.display = 'none'} />
                                                ) : <div className="w-10 h-10 bg-white/5 rounded-full shrink-0" />}
                                            </div>

                                            {/* Time Box */}
                                            <div className="flex flex-col items-center justify-center min-w-[120px] shrink-0">
                                                <div className={`px-4.5 py-1.5 border font-black text-2xl md:text-3xl rounded-xl mb-1 shadow-inner ${
                                                    isEsports 
                                                        ? 'bg-purple-500/20 text-cyan-300 border-cyan-500/25 shadow-[0_0_10px_rgba(6,182,212,0.15)]' 
                                                        : 'bg-purple-600/20 text-purple-300 border-purple-500/30'
                                                }`}>
                                                    {match.match_time ? match.match_time.slice(0, 5) : '--:--'}
                                                </div>
                                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">{match.field ? `${t('presentation_page.tv_field') || 'Veld'} ${match.field}` : `${t('presentation_page.tv_field') || 'Veld'}?`}</span>
                                            </div>

                                            {/* Away Team */}
                                            <div className="flex items-center justify-start gap-4 min-w-0">
                                                {match.away_team?.logo_url ? (
                                                    <img src={match.away_team.logo_url} className="w-10 h-10 md:w-12 md:h-12 object-contain bg-white/5 rounded-lg p-0.5 shrink-0 shadow-md animate-fade-in" onError={(e) => e.target.style.display = 'none'} />
                                                ) : <div className="w-10 h-10 bg-white/5 rounded-full shrink-0" />}
                                                <span className="text-xl md:text-2xl font-bold text-gray-300 block leading-tight text-left truncate">
                                                    {match.away_team?.name || 'Away'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                {matches.filter(m => m.score_home === null).length === 0 && (
                                    <div className="text-center py-12">
                                        <p className="text-xl text-gray-500 font-bold">{t('presentation_page.tv_no_matches') || 'Geen komende wedstrijden.'}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Sidebar (Rotator) */}
                <div className="hidden xl:flex flex-col w-72 shrink-0">
                    <div className={`h-full backdrop-blur-xl rounded-3xl p-6 border shadow-2xl flex flex-col justify-center transition-all ${
                        isEsports 
                            ? 'bg-purple-950/15 border-purple-500/20 shadow-[0_0_40px_rgba(168,85,247,0.15)]' 
                            : 'bg-white/5 border-white/10'
                    }`}>
                        {theme.logoRight && (() => {
                            const parseImages = (jsonString) => {
                                try {
                                    if (jsonString.startsWith('[')) return JSON.parse(jsonString);
                                    return [jsonString];
                                } catch (e) { return [jsonString]; }
                            };
                            const images = parseImages(theme.logoRight);
                            if (images.length > 0) return <SidebarRotator images={images} type="sponsor" visibleCount={2} />;
                            return null;
                        })()}
                    </div>
                </div>
            </div>

            {/* Subtle Controls Overlay */}
            <div className="absolute top-6 left-6 z-50 flex items-center gap-3 bg-black/40 backdrop-blur-md px-3.5 py-2 rounded-2xl border border-white/5 shadow-lg">
                <button 
                    onClick={() => setIsUnmuted(prev => !prev)}
                    className="text-gray-400 hover:text-white transition-colors p-1"
                    title={isUnmuted ? "Dempen / Mute" : "Geluid inschakelen / Unmute"}
                >
                    {isUnmuted ? <Volume2 size={18} /> : <VolumeX size={18} className="text-red-400 animate-pulse" />}
                </button>
                <div className="w-[1px] h-4 bg-white/10"></div>
                <button 
                    onClick={toggleFullscreen}
                    className="text-gray-400 hover:text-white transition-colors p-1"
                    title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                    {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                </button>
            </div>

            {/* LIVE NEWS TICKER FOOTER */}
            <div className={`absolute bottom-0 left-0 w-full h-16 border-t flex items-center z-50 overflow-hidden shadow-2xl transition-colors ${
                isEsports ? 'bg-[#06040c] border-purple-500/20' : 'bg-[#111] border-white/10'
            }`}>
                <style>{`
                    @keyframes scroll-left {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                `}</style>
                <div className={`px-6 h-full flex items-center justify-center shrink-0 z-20 shadow-lg relative overflow-hidden ${
                    isEsports ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'bg-red-600'
                }`}>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" style={{ animationDuration: '2s' }}></div>
                    <span className="font-black text-white uppercase tracking-widest text-sm animate-pulse shadow-sm">{t('presentation_page.tv_live') || 'Live'}</span>
                </div>
                <div className="flex-1 overflow-hidden relative h-full flex items-center">
                    <div className="whitespace-nowrap flex items-center gap-16 text-lg font-bold text-white pl-[100%]" style={{ animation: 'scroll-left 55s linear infinite' }}>
                        {/* Duplicate ticker items array to ensure a seamless infinite scrolling loop */}
                        {[...getNewsTickerItems(), ...getNewsTickerItems()].map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                                {idx > 0 && <span className={`w-2 h-2 rounded-full mx-2 animate-pulse ${
                                    isEsports ? 'bg-purple-500 shadow-[0_0_10px_#a855f7]' : 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]'
                                }`}></span>}

                                {item.type === 'text' && <span>{item.content}</span>}

                                {item.type === 'match' && (
                                    <div className="flex items-center gap-3 bg-white/5 px-4 py-1.5 rounded-full border border-white/10 backdrop-blur-md">
                                        <span className="text-gray-400 text-xs uppercase tracking-wider font-medium">{t('presentation_page.tv_result') || 'Uitslag'}:</span>
                                        <div className="flex items-center gap-2">
                                            {item.logo_home && <img src={item.logo_home} className="w-5 h-5 object-contain" onError={(e) => e.target.style.display = 'none'} />}
                                            <span className={item.score_home > item.score_away ? 'text-green-400 font-bold' : 'text-gray-200'}>{item.home}</span>
                                        </div>
                                        <span className={`font-black px-2 py-0.5 rounded shadow-sm text-sm ${
                                            isEsports ? 'bg-purple-500 text-white' : 'bg-yellow-500 text-black'
                                        }`}>
                                            {item.score_home}-{item.score_away}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <span className={item.score_away > item.score_home ? 'text-green-400 font-bold' : 'text-gray-200'}>{item.away}</span>
                                            {item.logo_away && <img src={item.logo_away} className="w-5 h-5 object-contain" onError={(e) => e.target.style.display = 'none'} />}
                                        </div>
                                    </div>
                                )}

                                {item.type === 'leader' && (
                                    <div className="flex items-center gap-2">
                                        <span>👑</span>
                                        <span className="text-gray-400">{t('presentation_page.tv_leader') || 'Koploper'} {item.poule}:</span>
                                        <span className={isEsports ? 'text-purple-300 shadow-purple-500/10' : 'text-yellow-400'}>{item.team}</span>
                                    </div>
                                )}

                                {item.type === 'sponsor_ad' && (
                                    <div className="flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-sm shadow-md">
                                        <span className="text-emerald-400 font-black uppercase tracking-wider text-xs">Partner:</span>
                                        {item.logo ? (
                                            <img src={item.logo} className="w-5 h-5 object-contain bg-white/10 rounded p-0.5" onError={(e) => e.target.style.display = 'none'} />
                                        ) : (
                                            <span>✨</span>
                                        )}
                                        <span className="text-white font-extrabold">{item.name}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
